import type { LocalMessage } from '@swchat/client-sdk';
import { useEffect, useState } from 'react';
import { Icon, IconButton } from '../design';
import { useClient } from '../lib/hooks';
import { formatBytes, isInlineImage } from '../lib/display';
import { useOpenImage } from './image-album';
import { LazyAudioPlayer } from './lazy';
import { downloadError, saveBlob } from './format';

/**
 * C2/W2.3: ảnh của tin file — tải qua SDK (GET /files cần header Authorization nên không dùng `<img src>` trỏ thẳng
 * server) → giải mã → object URL, thu hồi khi unmount hoặc đổi file. Trả `[url, lỗi]`; `enabled` false thì không tải.
 */
export function useDecryptedImage(m: LocalMessage, enabled: boolean): [string | undefined, string | undefined] {
  const client = useClient();
  const file = m.file!;
  const [url, setUrl] = useState<string>();
  const [error, setError] = useState<string>();
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    let objectUrl: string | undefined;
    setError(undefined);
    client
      .downloadFile(m)
      .then((bytes) => {
        if (!alive) return;
        objectUrl = URL.createObjectURL(new Blob([bytes as BlobPart], { type: file.mime }));
        setUrl(objectUrl);
      })
      .catch((e) => alive && setError(downloadError(e)));
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setUrl(undefined);
    };
    // Tải lại chỉ khi đổi file (danh sách tin tải lại liên tục vì reaction/receipt).
  }, [client, file.file_id, enabled]);
  return [url, error];
}

/**
 * C2: nội dung tin file. Ảnh: tự tải và giải mã (`useDecryptedImage`). File khác: tên, kích thước, nút tải.
 */
export function FileContent({ m, noAccess }: { m: LocalMessage; noAccess: boolean }) {
  const file = m.file!;
  const client = useClient();
  const openImage = useOpenImage();
  const image = isInlineImage(file);
  const audio = file.mime.startsWith('audio/');
  /** Nhạc chỉ tải + giải mã khi bấm phát lần đầu (file có thể nặng). */
  const [wantAudio, setWantAudio] = useState(false);
  const [url, imageError] = useDecryptedImage(m, (image || (audio && wantAudio)) && !noAccess);
  const [error, setError] = useState<string>();
  const [progress, setProgress] = useState<number>();

  async function download() {
    setError(undefined);
    setProgress(0);
    try {
      const bytes = await client.downloadFile(m, { onProgress: setProgress });
      saveBlob(
        new Blob([bytes as BlobPart], {
          type: file.mime || 'application/octet-stream',
        }),
        file.name,
      );
    } catch (e) {
      setError(downloadError(e));
    } finally {
      setProgress(undefined);
    }
  }

  if (noAccess) {
    return (
      <div className="ws-file" data-testid="msg-file" data-file-name={file.name}>
        <span className="ws-file-icon">
          <Icon name="file" size={20} />
        </span>
        <span className="ws-file-info">
          <span className="ws-file-name">{file.name}</span>
          <span className="ws-muted ws-small" data-testid="file-no-access">
            Không còn quyền tải
          </span>
        </span>
      </div>
    );
  }
  if (image) {
    const ratio = file.width && file.height ? `${file.width} / ${file.height}` : undefined;
    return (
      <div className="ws-file-image">
        {url && openImage ? (
          <button type="button" className="ws-image-open" aria-label={`Xem ảnh ${file.name}`} data-testid="msg-image-open" onClick={() => openImage(m.id)}>
            <img data-testid="msg-image" src={url} alt={file.name} width={file.width} height={file.height} />
          </button>
        ) : url ? (
          <img data-testid="msg-image" src={url} alt={file.name} width={file.width} height={file.height} />
        ) : (
          <div className="ws-image-placeholder ws-muted ws-small" style={{ aspectRatio: ratio }}>
            {imageError ?? 'Đang tải ảnh…'}
          </div>
        )}
      </div>
    );
  }
  if (audio) {
    return (
      <LazyAudioPlayer
        m={m}
        url={url}
        error={imageError}
        wanted={wantAudio}
        onWant={() => setWantAudio(true)}
        onDownload={() => void download()}
        downloading={progress !== undefined}
      />
    );
  }
  return (
    <div className="ws-file" data-testid="msg-file" data-file-name={file.name}>
      <span className="ws-file-icon">
        <Icon name="file" size={20} />
      </span>
      <span className="ws-file-info">
        <span className="ws-file-name" data-testid="file-name">
          {file.name}
        </span>
        <span className="ws-muted ws-small" data-testid="file-size">
          {progress !== undefined ? `Đang tải… ${Math.round(progress * 100)}%` : (error ?? formatBytes(file.size))}
        </span>
      </span>
      <IconButton
        label={`Tải xuống ${file.name}`}
        icon="download"
        size={18}
        className="ws-file-dl"
        data-testid="file-download"
        disabled={progress !== undefined}
        onClick={() => void download()}
      />
    </div>
  );
}
